import React, { useState } from 'react';
import { Logo, GradientBackground } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const PROS_A = [
  { icon: '👨‍🍳', label: 'Cook' },
  { icon: '🧹', label: 'House Cleaning' },
  { icon: '🧽', label: 'Deep Clean' },
  { icon: '🧺', label: 'Laundry' },
];
const PROS_B = [
  { icon: '🍼', label: 'Baby Care' },
  { icon: '🧑‍🦽', label: 'Elder Care' },
  { icon: '🔧', label: 'Handyman' },
  { icon: '🪴', label: 'Gardening' },
];

// Seamless auto-scrolling row of profession cards.
function ProMarquee({ items, direction = 'left', duration = 17 }) {
  const row = [...items, ...items, ...items, ...items];
  return (
    <div style={{ overflow: 'hidden', maskImage: 'linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)' }}>
      <div style={{
        display: 'flex', width: 'max-content',
        animation: `marquee-${direction} ${duration}s linear infinite`,
      }}>
        {row.map((p, i) => (
          <div key={`${p.label}-${i}`} style={{
            width: 116, margin: '0 6px', flex: 'none',
            background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 'var(--r-lg)', padding: '14px 8px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 26 }}>{p.icon}</div>
            <div style={{ color: '#fff', fontSize: 12, fontWeight: 700, marginTop: 6 }}>{p.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const glassInput = {
  flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none',
  padding: '15px 0', fontSize: 17, color: '#fff', letterSpacing: 1,
};
const inputRow = {
  display: 'flex', alignItems: 'center',
  background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.26)',
  borderRadius: 'var(--r-md)', padding: '0 16px',
};

function CtaButton({ title, onClick, disabled, hero, type = 'button', style }) {
  if (hero) {
    return (
      <div style={{ position: 'relative', alignSelf: 'stretch', ...style }}>
        <span style={{
          position: 'absolute', inset: -6, borderRadius: 'var(--r-pill)',
          background: 'var(--accent)', filter: 'blur(14px)', opacity: 0.4,
          animation: 'pulse-halo 2.6s ease-in-out infinite', pointerEvents: 'none',
        }} />
        <button type={type} onClick={onClick} disabled={disabled} className="btn btn--light"
          style={{ position: 'relative', gap: 10 }}>
          {title}
          <span style={{
            width: 26, height: 26, borderRadius: 13, background: 'var(--primary)', color: '#fff',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900,
          }}>→</span>
        </button>
      </div>
    );
  }
  return (
    <button type={type} onClick={onClick} disabled={disabled} className="btn btn--light" style={style}>
      {title}
    </button>
  );
}

export default function ProLogin({ onBackToCustomer }) {
  const { requestOtp, verifyOtp } = useAuth();
  const { toast } = useToast();
  const [screen, setScreen] = useState('intro'); // 'intro' | 'form'
  const [step, setStep] = useState('mobile');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [referral, setReferral] = useState('');
  const [loading, setLoading] = useState(false);

  const sendOtp = async (e) => {
    e?.preventDefault();
    if (mobile.length !== 10) return toast('Enter a valid 10-digit number', { tone: 'error' });
    setLoading(true);
    try {
      const res = await requestOtp(mobile, 'staff', referral.trim().toUpperCase() || undefined);
      if (res?.devOtp) setOtp(res.devOtp);
      setStep('otp');
    } catch (err) {
      toast(err.message || 'Please check your connection and try again.', { title: 'Could not send OTP', tone: 'error' });
    }
    setLoading(false);
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (otp.length < 4) return toast('Enter the OTP', { tone: 'error' });
    setLoading(true);
    try {
      await verifyOtp(mobile, otp);
    } catch (err) {
      toast(err.message || 'Incorrect OTP or server unreachable.', { title: 'Login failed', tone: 'error' });
    }
    setLoading(false);
  };

  return (
    <GradientBackground>
      <button onClick={onBackToCustomer} style={{
        position: 'absolute', top: 'calc(var(--sat) + 16px)', left: 16, zIndex: 10,
        background: 'rgba(0,0,0,0.18)', border: '1px solid rgba(255,255,255,0.25)',
        borderRadius: 'var(--r-pill)', padding: '8px 14px',
        color: '#fff', fontSize: 12.5, fontWeight: 700,
      }}>
        ← I'm here to hire
      </button>

      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
        padding: 'calc(var(--sat) + 64px) 22px calc(var(--sab) + 40px)',
        maxWidth: 520, margin: '0 auto', width: '100%', textAlign: 'center',
      }}>
        {screen === 'intro' ? (
          <>
            <div style={{
              background: 'rgba(232,163,61,0.22)', border: '1px solid var(--accent)',
              borderRadius: 'var(--r-pill)', padding: '6px 14px',
            }}>
              <span style={{ color: '#FCD98A', fontSize: 11.5, fontWeight: 800, letterSpacing: 1.5 }}>★ PARTNER PROGRAM</span>
            </div>

            <div className="float" style={{
              width: 84, height: 84, borderRadius: '50%', background: 'rgba(255,255,255,0.14)',
              border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 40, marginTop: 22,
            }}>💼</div>

            <h1 style={{ color: '#fff', fontSize: 28, fontWeight: 800, margin: '22px 0 0', lineHeight: 1.25 }}>
              Would you like to<br />join our team?
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14.5, marginTop: 10, lineHeight: 1.5 }}>
              Turn your skills into steady income. Verified jobs, on-time payouts and customers right in your area.
            </p>

            <div style={{ marginTop: 26, width: '100%' }}>
              <ProMarquee items={PROS_A} direction="left" duration={17} />
              <div style={{ height: 12 }} />
              <ProMarquee items={PROS_B} direction="right" duration={20} />
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 26, width: '100%' }}>
              {[['₹18k+', 'avg / month'], ['On-time', 'payouts'], ['Free', 'to join']].map(([a, b]) => (
                <div key={b} style={{
                  flex: 1, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)',
                  borderRadius: 'var(--r-md)', padding: '12px 6px',
                }}>
                  <div style={{ color: '#fff', fontSize: 15, fontWeight: 800 }}>{a}</div>
                  <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11, marginTop: 2 }}>{b}</div>
                </div>
              ))}
            </div>

            <CtaButton hero title="Continue" onClick={() => setScreen('form')} style={{ marginTop: 32 }} />
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11.5, marginTop: 18 }}>
              Free registration · KYC verified · You choose your jobs
            </p>
          </>
        ) : (
          <>
            <div style={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
              <Logo size="lg" />
              <div style={{
                background: 'var(--accent)', color: '#3A2708', borderRadius: 'var(--r-pill)',
                padding: '4px 14px', fontSize: 10.5, fontWeight: 900, letterSpacing: 1.5, marginTop: 10,
              }}>PRO PARTNER</div>
            </div>

            <h1 style={{ color: '#fff', fontSize: 25, fontWeight: 800, margin: '20px 0 0' }}>Partner Sign-in</h1>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13.5, marginTop: 8 }}>
              {step === 'mobile' ? 'Verify your number to access your jobs & earnings' : `Enter the OTP sent to +91 ${mobile}`}
            </p>

            <div style={{
              marginTop: 22, width: '100%', textAlign: 'left',
              background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.22)',
              borderRadius: 26, padding: 22, boxShadow: '0 14px 24px rgba(0,0,0,0.2)',
            }}>
              {step === 'mobile' ? (
                <form onSubmit={sendOtp}>
                  <div style={inputRow}>
                    <span style={{ color: '#fff', fontSize: 15.5, fontWeight: 700 }}>🇮🇳 +91</span>
                    <span style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.3)', margin: '0 12px' }} />
                    <input style={glassInput} placeholder="00000 00000" inputMode="numeric" autoComplete="tel"
                      maxLength={10} value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))} />
                  </div>
                  <div style={{ ...inputRow, marginTop: 12 }}>
                    <span style={{ color: '#fff', fontSize: 15.5 }}>🎁</span>
                    <span style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.3)', margin: '0 12px' }} />
                    <input style={glassInput} placeholder="Referral code (optional)" maxLength={10} value={referral}
                      onChange={(e) => setReferral(e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase())} />
                  </div>
                  <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11.5, margin: '10px 0 14px' }}>
                    Joined through a friend? Add their code to link up.
                  </p>
                  <CtaButton type="submit" title={loading ? 'Sending…' : 'Get OTP  →'} disabled={loading} />
                </form>
              ) : (
                <form onSubmit={verify}>
                  <input
                    style={{
                      width: '100%', background: 'rgba(255,255,255,0.14)',
                      border: '1px solid rgba(255,255,255,0.26)', borderRadius: 'var(--r-md)',
                      padding: '16px 0', fontSize: 22, color: '#fff', textAlign: 'center',
                      letterSpacing: 10, fontWeight: 700, outline: 'none',
                    }}
                    placeholder="••••••" inputMode="numeric" autoComplete="one-time-code"
                    maxLength={6} value={otp} autoFocus
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  />
                  <CtaButton type="submit" title={loading ? 'Verifying…' : 'Verify & Continue'} disabled={loading} style={{ marginTop: 18 }} />
                  <button type="button" onClick={() => setStep('mobile')}
                    style={{ marginTop: 16, width: '100%', color: '#fff', fontWeight: 700, opacity: 0.9 }}>
                    ← Change number
                  </button>
                </form>
              )}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8, marginTop: 22 }}>
              {['🛡️ KYC Verified', '💰 Weekly payouts', '📍 Jobs near you'].map((t) => (
                <span key={t} style={{
                  background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)',
                  padding: '7px 12px', borderRadius: 'var(--r-pill)',
                  color: 'rgba(255,255,255,0.92)', fontSize: 12, fontWeight: 600,
                }}>{t}</span>
              ))}
            </div>

            <button onClick={() => { setScreen('intro'); setStep('mobile'); }}
              style={{ marginTop: 20, color: '#fff', fontWeight: 700, opacity: 0.9 }}>← Back</button>
          </>
        )}
      </div>
    </GradientBackground>
  );
}
